<template>
    <div v-if="visible" class="user-select-modal-overlay" @click="closeModal">
        <div class="user-select-modal" @click.stop>
            <div class="modal-header">
                <h3>{{ title || '选择用户' }}</h3>
                <button class="close-btn" @click="closeModal">
                    <i class="el-icon-close"></i>
                </button>
            </div>


            <!-- 状态信息栏 -->
            <div class="user-select-status">
                <div class="multi-select-actions">
                    <div class="search-input-container">
                        <input type="text" class="search-input" placeholder="输入用户ID搜索..." v-model="searchId"
                            @keyup.enter="searchUserById" @input="handleSearchInput" />
                        <button class="search-btn" @click="searchUserById" :disabled="!searchId.trim()">
                            <i class="el-icon-search"></i>
                        </button>
                    </div>
                    <div class="select-info">
                        已选择 {{ selectedUsers.length }} 个用户
                    </div>
                    <div class="select-actions">
                        <button class="btn btn-link" @click="selectAllUsers">
                            全选
                        </button>
                        <button class="btn btn-link" @click="clearSelection">
                            取消全选
                        </button>
                    </div>
                </div>
            </div>

            <div class="modal-content">
                <div class="user-select-content">
                    <div class="user-grid-container">
                        <ul class="user-grid" v-infinite-scroll="loadMoreUsers"
                            :infinite-scroll-disabled="loading || noMoreData" :infinite-scroll-distance="10"
                            :infinite-scroll-immediate="false" style="overflow: auto;height: calc(100vh - 500px);">
                            <li v-for="(user, index) in availableUsers" :key="user.id" class="user-select-item"
                                :class="{ 'selected': selectedUsers.some(selected => selected.id === user.id) }"
                                @click="selectUser(user)">
                                <div class="user-avatar">
                                    <img v-if="user.personal && user.personal.avatar_xl" :src="user.personal.avatar_xl"
                                        :alt="user.personal.name" />
                                    <div v-else class="default-avatar">
                                        <i class="el-icon-user"></i>
                                    </div>
                                    <div class="selection-indicator">
                                        <i v-if="selectedUsers.some(selected => selected.id === user.id)"
                                            class="el-icon-check"></i>
                                    </div>
                                </div>
                                <div class="user-name">{{ user.personal && user.personal.name ? user.personal.name :
                                    '未知用户' }}</div>
                                <div class="user-id">ID: {{ user.id }}</div>
                            </li>

                            <!-- 加载状态 -->
                            <li v-if="loading" class="loading-container">
                                <i class="el-icon-loading"></i>
                                <span>加载中...</span>
                            </li>

                            <!-- 没有更多数据 -->
                            <li v-if="noMoreData && availableUsers.length > 0" class="no-more-data">
                                <span>没有更多数据了</span>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            <!-- 操作按钮 -->
            <div class="user-select-actions">
                <button class="btn btn-secondary" @click="closeModal">
                    取消
                </button>
                <button class="btn btn-primary" @click="confirmSelection" :disabled="selectedUsers.length === 0">
                    {{ confirmText || '确认选择' }} ({{ selectedUsers.length }})
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { getChatDialogsByCriteria, getChatDialogsByPairs } from '@/api/talkytimes/message'
import { getConnectionsProfiles } from '@/api/talkytimes/personalData'

export default {
    name: 'UserSelectModal',
    props: {
        visible: {
            type: Boolean,
            default: false
        },
        title: {
            type: String,
            default: '选择用户'
        },
        confirmText: {
            type: String,
            default: '确认选择'
        },
        excludeUserIds: {
            type: Array,
            default: () => []
        },
        multiple: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            availableUsers: [],
            selectedUsers: [],
            // 分页相关
            loading: false,
            noMoreData: false,
            currentPage: 1,
            paramJson: {
                criteria: [""],
                cursor: "",
                limit: 20
            },
            // 搜索相关
            searchId: '',
            isSearching: false,
            searchResults: []
        }
    },
    watch: {
        visible(newVal) {
            if (newVal) {
                this.initModal()
            } else {
                this.resetModal()
            }
        }
    },
    methods: {
        // 初始化弹窗
        initModal() {
            this.resetModal()
            this.loadMoreUsers()
        },

        // 重置弹窗状态
        resetModal() {
            this.availableUsers = []
            this.selectedUsers = []
            this.currentPage = 1
            this.loading = false
            this.noMoreData = false
            this.paramJson.cursor = ""
            // 重置搜索状态
            this.searchId = ''
            this.isSearching = false
            this.searchResults = []
        },

        // 关闭弹窗
        closeModal() {
            this.$emit('close')
        },

        // 加载更多用户数据
        async loadMoreUsers() {
            if (this.loading || this.noMoreData) {
                return
            }

            try {
                this.loading = true

                const paramJson = this.paramJson
                const response = await getChatDialogsByCriteria(paramJson)

                console.log('聊天对话数据:', response)
                this.paramJson.cursor = response.cursor

                if (response && response.dialogs) {
                    const dialogs = response.dialogs || []

                    if (dialogs.length > 0) {
                        const profiles = await getConnectionsProfiles({ ids: dialogs.map(dialog => dialog.idInterlocutor) })
                        console.log('用户资料数据:', profiles.data.profiles)

                        let profilesData = profiles.data?.profiles || []

                        if (Array.isArray(profilesData) && profilesData.length > 0) {
                            // 过滤掉排除的用户
                            const newUsers = profilesData.filter(user => {
                                return !this.excludeUserIds.includes(user.id) &&
                                    !this.availableUsers.find(availableUser => availableUser.id === user.id)
                            })

                            // 添加到现有用户列表
                            this.availableUsers.push(...newUsers)

                            // 检查是否还有更多数据
                            if (newUsers.length < this.paramJson.limit) {
                                this.noMoreData = true
                            }

                            this.currentPage++
                        } else {
                            this.noMoreData = true
                        }
                    } else {
                        this.noMoreData = true
                    }
                } else {
                    this.noMoreData = true
                }
            } catch (error) {
                console.error('加载用户数据失败:', error)
                this.$message.error('加载用户数据失败')
            } finally {
                this.loading = false
            }
        },

        // 选择用户
        selectUser(user) {
            if (!this.multiple) {
                // 单选模式
                this.selectedUsers = [{
                    id: user.id,
                    userId: user.id,
                    nickname: user.personal?.name || '未知用户',
                    username: user.personal?.name || '未知用户',
                    avatar: user.personal?.avatar_xl || null
                }]
                return
            }

            // 多选模式
            const index = this.selectedUsers.findIndex(selected => selected.id === user.id)
            if (index > -1) {
                // 如果已选中，则取消选中
                this.selectedUsers.splice(index, 1)
            } else {
                // 如果未选中，则添加到选中列表，保存完整的用户信息
                this.selectedUsers.push({
                    id: user.id,
                    userId: user.id,
                    nickname: user.personal?.name || '未知用户',
                    username: user.personal?.name || '未知用户',
                    avatar: user.personal?.avatar_xl || null
                })
            }
        },

        // 确认选择
        confirmSelection() {
            if (this.selectedUsers.length === 0) {
                this.$message.warning('请先选择用户')
                return
            }

            this.$emit('confirm', this.selectedUsers)
        },

        // 全选用户
        selectAllUsers() {
            if (!this.multiple) return

            this.selectedUsers = this.availableUsers.map(user => ({
                id: user.id,
                userId: user.id,
                nickname: user.personal?.name || '未知用户',
                username: user.personal?.name || '未知用户',
                avatar: user.personal?.avatar_xl || null
            }))
        },

        // 取消全选
        clearSelection() {
            this.selectedUsers = []
        },

        // 处理搜索输入
        handleSearchInput() {
            // 如果输入为空，恢复显示所有用户
            if (!this.searchId.trim()) {
                this.isSearching = false
                this.searchResults = []
                // 重新加载用户列表
                this.resetModal()
                this.loadMoreUsers()
            }
        },

        // 根据ID搜索用户
        async searchUserById() {
            const userId = this.searchId.trim()
            if (!userId) {
                this.$message.warning('请输入用户ID')
                return
            }

            // 验证ID是否为数字
            if (!/^\d+$/.test(userId)) {
                this.$message.warning('请输入有效的用户ID')
                return
            }

            try {
                this.isSearching = true
                this.loading = true

                // 调用搜索接口
                const searchData = {
                    idsRegularUser: [parseInt(userId)]
                }

                const response = await getChatDialogsByPairs(searchData)
                console.log('搜索用户响应:', response)

                if (response && response.length > 0) {
                    // 获取用户详细信息
                    const profiles = await getConnectionsProfiles({
                        ids: response.map(dialog => dialog.idInterlocutor)
                    })

                    let profilesData = profiles.data?.profiles || []

                    if (Array.isArray(profilesData) && profilesData.length > 0) {
                        // 过滤掉排除的用户
                        const filteredUsers = profilesData.filter(user => {
                            return !this.excludeUserIds.includes(user.id)
                        })

                        this.searchResults = filteredUsers
                        this.availableUsers = filteredUsers
                        this.noMoreData = true // 搜索结果不需要分页

                        if (filteredUsers.length === 0) {
                            this.$message.info('未找到该用户或用户已添加')
                        } else {
                            this.$message.success(`找到 ${filteredUsers.length} 个用户`)
                        }
                    } else {
                        this.availableUsers = []
                        this.searchResults = []
                        this.$message.info('未找到该用户1')
                    }
                } else {
                    this.availableUsers = []
                    this.searchResults = []
                    this.$message.info('未找到该用户2')
                }
            } catch (error) {
                console.error('搜索用户失败:', error)
                this.$message.error('搜索用户失败')
                this.availableUsers = []
                this.searchResults = []
            } finally {
                this.loading = false
            }
        }
    }
}
</script>

<style scoped>
/* 用户选择弹窗样式 */
.user-select-modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1100;
    backdrop-filter: blur(5px);
}

.user-select-modal {
    background: white;
    border-radius: 12px;
    width: 90%;
    max-width: 800px;
    max-height: 85vh;
    overflow: hidden;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
    animation: modalSlideIn 0.3s ease-out;
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 24px;
    border-bottom: 1px solid rgba(229, 231, 235, 0.8);
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
}

.modal-header h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 700;
    color: #1f2937;
    background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
}

.close-btn {
    width: 32px;
    height: 32px;
    border: none;
    background: rgba(107, 114, 128, 0.1);
    color: #6b7280;
    border-radius: 8px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
}

.close-btn:hover {
    background: rgba(107, 114, 128, 0.2);
    color: #374151;
    transform: scale(1.1);
}

.modal-content {
    overflow: hidden;
}

.user-select-content {
    padding: 24px 0px;
    overflow-y: auto;
}

.empty-users {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
    color: #9ca3af;
    text-align: center;
}

.empty-users i {
    font-size: 48px;
    margin-bottom: 16px;
    opacity: 0.5;
}

.empty-users p {
    font-size: 16px;
    font-weight: 500;
    margin: 0;
    color: #6b7280;
}

.user-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 16px;
    overflow-y: auto;
    list-style: none;
    padding: 0 24px;
    margin: 0;
}

.user-select-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 16px;
    border-radius: 12px;
    background: white;
    border: 2px solid rgba(229, 231, 235, 0.8);
    cursor: pointer;
    transition: all 0.3s ease;
    position: relative;
}

.user-select-item:hover {
    border-color: rgba(139, 92, 246, 0.3);
    box-shadow: 0 4px 12px rgba(139, 92, 246, 0.1);
    transform: translateY(-2px);
}

.user-select-item.selected {
    border-color: #8b5cf6;
    background: rgba(139, 92, 246, 0.05);
    box-shadow: 0 4px 12px rgba(139, 92, 246, 0.2);
}

.user-select-item .user-avatar {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    overflow: hidden;
    margin-bottom: 12px;
    border: 3px solid rgba(229, 231, 235, 0.8);
    background: #f8fafc;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    transition: all 0.3s ease;
}

.user-select-item.selected .user-avatar {
    border-color: #8b5cf6;
    transform: scale(1.05);
}

.user-select-item .user-avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.user-select-item .default-avatar {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
    color: white;
}

.user-select-item .default-avatar i {
    font-size: 32px;
}

.user-select-item .selection-indicator {
    position: absolute;
    top: 8px;
    right: 8px;
    width: 24px;
    height: 24px;
    background: #8b5cf6;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 12px;
    opacity: 0;
    transition: opacity 0.3s ease;
}

.user-select-item.selected .selection-indicator {
    opacity: 1;
}

.user-select-item .user-name {
    font-weight: 600;
    font-size: 14px;
    color: #1f2937;
    margin-bottom: 4px;
    text-align: center;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.user-select-item .user-id {
    font-size: 12px;
    color: #6b7280;
    font-weight: 500;
    text-align: center;
}

.user-select-actions {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    padding: 20px 24px;
    border-top: 1px solid rgba(229, 231, 235, 0.8);
    background: rgba(248, 250, 252, 0.8);
}

.user-select-actions .btn {
    padding: 10px 20px;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
}

.user-select-actions .btn-secondary {
    background: rgba(107, 114, 128, 0.1);
    color: #6b7280;
}

.user-select-actions .btn-secondary:hover {
    background: rgba(107, 114, 128, 0.2);
    transform: translateY(-1px);
}

.user-select-actions .btn-primary {
    background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
    color: white;
    box-shadow: 0 4px 15px rgba(139, 92, 246, 0.3);
}

.user-select-actions .btn-primary:hover {
    background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%);
    box-shadow: 0 6px 20px rgba(139, 92, 246, 0.4);
    transform: translateY(-1px);
}

.user-select-actions .btn-primary:disabled {
    background: rgba(107, 114, 128, 0.3);
    color: rgba(255, 255, 255, 0.5);
    cursor: not-allowed;
    box-shadow: none;
    transform: none;
}

/* 加载状态样式 */
.loading-container {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    color: #6b7280;
    font-size: 14px;
    grid-column: 1 / -1;
    text-align: center;
}

.loading-container i {
    margin-right: 8px;
    font-size: 16px;
    animation: rotate 1s linear infinite;
}

@keyframes rotate {
    from {
        transform: rotate(0deg);
    }

    to {
        transform: rotate(360deg);
    }
}

/* 没有更多数据样式 */
.no-more-data {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    color: #9ca3af;
    font-size: 14px;
    border-top: 1px solid rgba(229, 231, 235, 0.8);
    margin-top: 16px;
    grid-column: 1 / -1;
    text-align: center;
}

.search-input-container {
    display: flex;
    align-items: center;
    gap: 12px;
    max-width: 400px;
}

.search-input {
    flex: 1;
    padding: 10px 16px;
    border: 2px solid rgba(229, 231, 235, 0.8);
    border-radius: 8px;
    font-size: 14px;
    background: white;
    transition: all 0.3s ease;
    outline: none;
}

.search-input:focus {
    border-color: #8b5cf6;
    box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
}

.search-input::placeholder {
    color: #9ca3af;
}

.search-btn {
    width: 40px;
    height: 40px;
    border: none;
    border-radius: 8px;
    background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
    color: white;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    box-shadow: 0 2px 8px rgba(139, 92, 246, 0.3);
}

.search-btn:hover:not(:disabled) {
    background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%);
    box-shadow: 0 4px 12px rgba(139, 92, 246, 0.4);
    transform: translateY(-1px);
}

.search-btn:disabled {
    background: rgba(107, 114, 128, 0.3);
    color: rgba(255, 255, 255, 0.5);
    cursor: not-allowed;
    box-shadow: none;
    transform: none;
}

.search-btn i {
    font-size: 16px;
}

/* 状态信息栏样式 */
.user-select-status {
    padding: 16px 24px;
    border-bottom: 1px solid rgba(229, 231, 235, 0.8);
    background: rgba(248, 250, 252, 0.8);
}

.user-select-status .empty-users {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 20px;
    color: #9ca3af;
    text-align: center;
}

.user-select-status .empty-users i {
    font-size: 32px;
    margin-bottom: 12px;
    opacity: 0.5;
}

.user-select-status .empty-users p {
    font-size: 14px;
    font-weight: 500;
    margin: 0;
    color: #6b7280;
}

/* 多选操作栏样式 */
.multi-select-actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0;
    background: transparent;
    border-radius: 0;
    margin-bottom: 0;
    border: none;
}

.select-info {
    font-size: 14px;
    font-weight: 500;
    color: #6b7280;
}

.select-actions {
    display: flex;
    gap: 12px;
}

.select-actions .btn-link {
    background: none;
    border: none;
    color: #8b5cf6;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    padding: 4px 8px;
    border-radius: 4px;
    transition: all 0.3s ease;
}

.select-actions .btn-link:hover {
    background: rgba(139, 92, 246, 0.1);
    color: #7c3aed;
}

@keyframes modalSlideIn {
    from {
        opacity: 0;
        transform: translateY(-20px) scale(0.95);
    }

    to {
        opacity: 1;
        transform: translateY(0) scale(1);
    }
}
</style>
